import { useEffect, useRef, useState } from "react";
import { arrayMove } from "react-sortable-hoc";
import { canManageSpace } from "@/react/helpers/spaceHelpers";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import { useCurrentSpaceContext } from "@circle-react/contexts";
import { useIsVisibleOnDOM } from "@circle-react/hooks/utils/useIsVisibleOnDOM";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { AddBookmarkButton } from "./AddBookmarkButton";
import { ScrollableContainerView } from "./ScrollableContainerView";
import { SortableList } from "./SortableList";
import { useBookmarks } from "./hooks/useBookmarks";

export const Bookmarks = () => {
  const [leftHookRef, isLeftEndVisible] = useIsVisibleOnDOM<HTMLDivElement>();
  const [rightHookRef, isRightEndVisible] = useIsVisibleOnDOM<HTMLDivElement>();
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const { data: space } = useCurrentSpaceContext();
  const {
    records,
    isLoading: isLoadingRecords,
    updateBookmarksOrder,
  } = useBookmarks({ space });
  const isBookmarksEmpty = !records || !records.length;
  const [orderedBookmarks, setOrderedBookmarks] = useState(records);

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const reorderedData = arrayMove(orderedBookmarks, oldIndex, newIndex);

    setOrderedBookmarks(reorderedData);
    updateBookmarksOrder({
      bookmarks_order: reorderedData.map(link => link.id.toString()),
    });
  };

  useEffect(() => {
    if (!isLoadingRecords) {
      setOrderedBookmarks(records);
    }
  }, [records, isLoadingRecords]);

  if (isBookmarksEmpty) {
    return null;
  }

  if (!space) {
    return null;
  }

  return (
    <BlockInViewOnlyMode>
      <ScrollableContainerView
        showLeftScroller={!isLeftEndVisible}
        showRightScroller={!isRightEndVisible}
        scrollableContainerRef={scrollableContainerRef}
        leftHookRef={leftHookRef}
        rightHookRef={rightHookRef}
        id="bookmarks-bar"
      >
        {isLoadingRecords ? (
          <SkeletonLoader variant="bookmarks" />
        ) : (
          <>
            <SortableList
              bookmarks={orderedBookmarks}
              axis="x"
              lockAxis="x"
              onSortEnd={onSortEnd}
              distance={10}
            />
            {canManageSpace(space) && (
              <AddBookmarkButton
                shouldShowLabel={isBookmarksEmpty}
                space={space}
              />
            )}
          </>
        )}
      </ScrollableContainerView>
    </BlockInViewOnlyMode>
  );
};
